import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowDown, X, MessageSquare, Paperclip, Search, Mic, Square, Send, Phone, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { getSDK } from '@/store/sdkStore';
import { listMessages as bridgeListMessages, listMessagesBefore as bridgeListMessagesBefore, markRead as bridgeMarkRead, reactToMessage as bridgeReact, forwardMessage as bridgeForward, clearConversation as bridgeClear } from '@/lib/chatBridge';
import { AppEmbedPreview } from '@/components/embed/AppEmbedPreview';
import { createLogger } from '@/lib/logger';
import { friendlyError } from '@/lib/errors';
import { eventBusHub } from '@/lib/eventBusHub';
import { confirmDialog } from '@/lib/dialogs';
import { EmojiPicker } from '@/components/ui/EmojiPicker';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { useIsDarkTheme } from '@/hooks/useIsDarkTheme';
import { useConnectionStore } from '@/lib/connection';
import { sendChatMessage, useChatQueueStore } from '@/lib/chatQueue';
import { uploadFile } from '@/lib/chunkedUpload';
import { useToastStore } from '@/store/toastStore';
import { fileBytes } from '@/lib/fileBytes';
import { FilePreviewModal } from '@/components/viewer/FilePreviewModal';
import { Virtuoso, type VirtuosoHandle, type ListRange } from 'react-virtuoso';
import { MessageBubble, type Msg as BubbleMsg } from '@/components/chat/MessageBubble';
import { VideoCallPanel } from '@/components/chat/VideoCallPanel';
import { RemotePanel } from '@/components/remote/RemotePanel';
import { publishRemoteSignal, remoteTopic } from '@/lib/remote/remoteSession';
import { messageEmbedStyle } from '@/lib/chatMessageLayout';

const log = createLogger('ChatThread');

export interface ThreadUser { id: string; name: string; avatarUrl?: string }

export interface ForwardTarget { id: string; title: string }

interface FileRef { id: string; name: string; size: number }
interface MsgReaction { emoji: string; userId: string }

interface Msg {
  id: string;
  conversationId: string;
  senderId: string;
  text: string;
  createdAt: string;
  isDeleted?: boolean;
  readBy?: string[];
  attachments?: FileRef[];
  replyTo?: string;
  reactions?: MsgReaction[];
}

// Пресенс собеседника: «онлайн» (вход ≤2 мин назад) / «был(а) …»
import { lastSeenLabel, QueuedIndicator } from './chatThreadParts';
interface ChatThreadProps {
  conversationId: string;
  title: string;
  /** Пресенс участников: id → last_seen_at (SSoT — сервер) */
  memberLastSeen?: Record<string, string>;
  /** Имена пользователей (если не переданы — грузятся сами через listEntities) */
  users?: ThreadUser[];
  myId: string;
  isAdmin: boolean;
  /** Чаты для пересылки (если не передано — кнопка «Переслать» скрыта) */
  forwardTargets?: ForwardTarget[];
  /** Кнопка «←» в шапке (мобильный список / контекстная панель) */
  onBack?: () => void;
  /** Авто-прочтение при открытии (default true) */
  markReadOnOpen?: boolean;
}


export const URL_RE = /https?:\/\/[^\s<>"')\]]+/i;

export function ChatEmbedLink({ text }: { text: string }) {
  const m = text.match(URL_RE);
  if (!m) return null;
  return (
    // Превью ссылки идёт в той же медиа-полосе, что и вложения сообщения
    // (не растягивается на всю ленту); отступ слева — колонка аватара.
    <div className="flex px-3 pt-1.5">
      <span className="mr-1.5 h-8 w-8 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1" style={messageEmbedStyle()}>
        <AppEmbedPreview source={{ kind: 'url', url: m[0] }} />
      </div>
    </div>
  );
}
